import {
  ArrowLeft,
  Bookmark,
  BellRing,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  User as UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import { Spinner } from '../components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import {
  useBulkDeleteUsers,
  useCancelDeletion,
  useUser,
  useUserBookmarks,
  useUserPushSubs,
  useUserReactions,
  useSetUserRole,
} from '../lib/api/users';
import { useAuthStore } from '../lib/auth/store';
import { fmtDateTime, fmtRelative } from '../lib/date';

export default function UserDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const user = useUser(id ?? null);

  return (
    <>
      <Topbar title={user.data?.name ?? user.data?.email ?? 'User'} />
      <div className="px-6 py-3">
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to users
        </Link>
      </div>
      {user.isLoading ? (
        <div className="px-6 pt-6">
          <Spinner label="Loading…" />
        </div>
      ) : user.data ? (
        <UserBody userId={user.data.id} />
      ) : (
        <div className="px-6 py-10">
          <Empty title="User not found" />
        </div>
      )}
    </>
  );
}

function UserBody({ userId }: { userId: string }): JSX.Element {
  const user = useUser(userId);
  const me = useAuthStore((s) => s.user);
  const setRole = useSetUserRole();
  const cancelDeletion = useCancelDeletion();
  const deleteUsers = useBulkDeleteUsers();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const u = user.data!;
  const isMe = me?.id === userId;

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            {u.avatarUrl ? (
              <img
                src={u.avatarUrl}
                alt=""
                className="h-10 w-10 rounded-full ring-2 ring-line"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted text-sm font-semibold uppercase text-ink-muted">
                {u.email.slice(0, 2)}
              </div>
            )}
            {u.name ?? u.email}
          </span>
        }
        subtitle={u.email}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={u.role === 'admin' ? 'brand' : 'neutral'} className="uppercase">
              <ShieldCheck className="h-3 w-3" /> {u.role}
            </Badge>
            {(['user', 'correspondent', 'admin'] as const).map((target) => {
              if (target === u.role) return null;
              // Self-demote guard mirrors the API's: an admin can't drop
              // themselves below admin via this UI.
              const selfDemote = isMe && u.role === 'admin' && target !== 'admin';
              return (
                <Button
                  key={target}
                  variant={target === 'admin' ? 'primary' : 'outline'}
                  size="sm"
                  disabled={setRole.isPending || selfDemote}
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Set ${u.email}'s role to "${target}"?`,
                        description:
                          target === 'admin'
                            ? 'Admins have full access to every part of the panel.'
                            : `Changes what ${u.email} can do in the panel.`,
                        confirmLabel: 'Change role',
                        variant: 'primary',
                      }))
                    )
                      return;
                    try {
                      await setRole.mutateAsync({ id: userId, role: target });
                      showToast({ title: `Role set to ${target}`, variant: 'success' });
                    } catch (err) {
                      showToast({ title: 'Failed', description: (err as Error).message, variant: 'danger' });
                    }
                  }}
                >
                  {target === 'admin'
                    ? 'Make admin'
                    : target === 'correspondent'
                      ? 'Make correspondent'
                      : 'Demote to user'}
                </Button>
              );
            })}
          </div>
        }
      />

      {u.deletionRequested ? (
        <div className="mx-6 mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-danger/30 bg-danger/[0.04] px-4 py-3 text-sm">
          <p className="min-w-[240px] flex-1 text-ink">
            <strong>Deletion requested.</strong> {u.email} deleted their account in the app. It’s
            signed out and can’t sign in. Delete it within 7 days — their live streams and reports
            go with it — or cancel the request.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={cancelDeletion.isPending || deleteUsers.isPending}
            onClick={async () => {
              try {
                await cancelDeletion.mutateAsync(userId);
                showToast({ title: 'Deletion request cancelled', variant: 'success' });
              } catch (err) {
                showToast({ title: 'Failed', description: (err as Error).message, variant: 'danger' });
              }
            }}
          >
            Cancel request
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-danger/40 text-danger"
            disabled={isMe || cancelDeletion.isPending || deleteUsers.isPending}
            onClick={async () => {
              if (
                !(await confirm({
                  title: `Permanently delete ${u.email}?`,
                  description:
                    'Deletes the account with its bookmarks, reactions, eyewitness reports and live streams. This cannot be undone.',
                  confirmLabel: 'Delete account',
                }))
              )
                return;
              try {
                await deleteUsers.mutateAsync([userId]);
                showToast({ title: 'Account deleted', variant: 'success' });
                navigate('/users');
              } catch (err) {
                showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
              }
            }}
          >
            Delete account
          </Button>
        </div>
      ) : null}

      <div className="px-6 pb-10">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">
              <UserIcon className="h-3.5 w-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="bookmarks">
              <Bookmark className="h-3.5 w-3.5" /> Bookmarks
            </TabsTrigger>
            <TabsTrigger value="reactions">
              <ThumbsUp className="h-3.5 w-3.5" /> Reactions
            </TabsTrigger>
            <TabsTrigger value="push">
              <BellRing className="h-3.5 w-3.5" /> Push subscriptions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab userId={userId} />
          </TabsContent>
          <TabsContent value="bookmarks">
            <BookmarksTab userId={userId} />
          </TabsContent>
          <TabsContent value="reactions">
            <ReactionsTab userId={userId} />
          </TabsContent>
          <TabsContent value="push">
            <PushTab userId={userId} />
          </TabsContent>
        </Tabs>
      </div>
      {confirmDialog}
    </>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewTab({ userId }: { userId: string }): JSX.Element {
  const user = useUser(userId);
  const bookmarks = useUserBookmarks(userId, 1, 1);
  const reactions = useUserReactions(userId, 1, 1);
  const pushSubs = useUserPushSubs(userId);
  const u = user.data!;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="ID" value={<span className="font-mono text-xs">{u.id}</span>} />
          <Row label="Email" value={u.email} />
          <Row label="Name" value={u.name ?? '—'} />
          <Row label="Role" value={
            <Badge variant={u.role === 'admin' ? 'brand' : 'neutral'} className="uppercase">
              {u.role}
            </Badge>
          } />
          <Row label="Joined" value={fmtDateTime(u.createdAt)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity summary</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <StatTile
            label="Bookmarks"
            value={bookmarks.isLoading ? '…' : String(bookmarks.data?.total ?? 0)}
          />
          <StatTile
            label="Reactions"
            value={reactions.isLoading ? '…' : String(reactions.data?.total ?? 0)}
          />
          <StatTile
            label="Push devices"
            value={pushSubs.isLoading ? '…' : String(pushSubs.data?.items.length ?? 0)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line pb-2 last:border-0 last:pb-0">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right font-medium text-ink">{value}</span>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-line bg-surface-muted px-3 py-3 text-center">
      <p className="text-2xl font-semibold tabular-nums text-ink">{value}</p>
      <p className="mt-0.5 text-xs text-ink-muted">{label}</p>
    </div>
  );
}

// ── Bookmarks ─────────────────────────────────────────────────────────────────

function BookmarksTab({ userId }: { userId: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const list = useUserBookmarks(userId, page, pageSize);
  const totalPages = Math.max(1, Math.ceil((list.data?.total ?? 0) / pageSize));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bookmarks</CardTitle>
        <CardDescription>{list.data?.total ?? 0} saved post(s)</CardDescription>
      </CardHeader>
      <CardContent>
        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : !list.data?.items.length ? (
          <Empty title="No bookmarks" description="This user hasn't saved any posts." />
        ) : (
          <>
            <table className="table-base">
              <thead>
                <tr>
                  <th>Post</th>
                  <th className="w-24">Locale</th>
                  <th className="w-36">Published</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link
                        to={`/posts/${encodeURIComponent(p.slug)}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {p.title}
                      </Link>
                    </td>
                    <td className="text-sm text-ink-muted">
                      {p.publishedAt ? fmtRelative(p.publishedAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Reactions ─────────────────────────────────────────────────────────────────

function ReactionsTab({ userId }: { userId: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const list = useUserReactions(userId, page, pageSize);
  const totalPages = Math.max(1, Math.ceil((list.data?.total ?? 0) / pageSize));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reactions</CardTitle>
        <CardDescription>{list.data?.total ?? 0} reaction(s)</CardDescription>
      </CardHeader>
      <CardContent>
        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : !list.data?.items.length ? (
          <Empty title="No reactions" description="This user hasn't reacted to any posts." />
        ) : (
          <>
            <table className="table-base">
              <thead>
                <tr>
                  <th>Post</th>
                  <th className="w-24">Locale</th>
                  <th className="w-24 text-center">Kind</th>
                  <th className="w-36">Reacted</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((r) => (
                  <tr key={r.post.id}>
                    <td>
                      <Link
                        to={`/posts/${encodeURIComponent(r.post.slug)}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {r.post.title}
                      </Link>
                    </td>
                    <td className="text-center">
                      {r.kind === 'like' ? (
                        <ThumbsUp className="mx-auto h-4 w-4 text-success" />
                      ) : (
                        <ThumbsDown className="mx-auto h-4 w-4 text-danger" />
                      )}
                    </td>
                    <td className="text-sm text-ink-muted">{fmtRelative(r.reactedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Push subscriptions ────────────────────────────────────────────────────────

const PLATFORM_LABEL: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

function PushTab({ userId }: { userId: string }): JSX.Element {
  const subs = useUserPushSubs(userId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Push subscriptions</CardTitle>
        <CardDescription>
          FCM device tokens associated with this account. Anonymous tokens (registered before
          sign-in) are not shown here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {subs.isLoading ? (
          <Spinner label="Loading…" />
        ) : !subs.data?.items.length ? (
          <Empty
            title="No push subscriptions"
            description="This user hasn't registered a device for push notifications."
          />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th className="w-28">Platform</th>
                <th className="w-20">Locale</th>
                <th className="w-40">Last seen</th>
                <th className="w-40">Registered</th>
              </tr>
            </thead>
            <tbody>
              {subs.data.items.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Badge variant="outline">{PLATFORM_LABEL[s.platform] ?? s.platform}</Badge>
                  </td>
                  <td>
                    {s.locale ? (
                      <Badge variant="outline" className="uppercase">{s.locale}</Badge>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </td>
                  <td className="text-sm text-ink-muted">{fmtRelative(s.lastSeenAt)}</td>
                  <td className="text-sm text-ink-muted">{fmtRelative(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}): JSX.Element | null {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-line pt-3 text-sm text-ink-muted">
      <span>Page {page} of {totalPages}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
